import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  isBlockAnswered,
  type NumberRequest,
  type UpdateNumberRequestData,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import { EditableBlock } from "./editable-block";

/**
 * The free-text block, last on the page because it is the only one nobody has
 * to fill in — except on a SIP request, where describing the setup *is* the
 * request, so there it is required and opens on its own.
 */
export function RequestNotes({
  request,
  isSaving,
  onSave,
  readOnly,
}: {
  request: NumberRequest;
  isSaving: boolean;
  onSave: (data: UpdateNumberRequestData) => Promise<boolean>;
  /** The file is closed: what was written is shown, nothing can be changed. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation("numberRequests");
  const [notes, setNotes] = useState(request.notes);

  useEffect(() => setNotes(request.notes), [request.notes]);

  const isSetup = request.kind === "sip_trunk";

  const submit = async (event: React.FormEvent, close: () => void) => {
    event.preventDefault();
    if (await onSave({ notes, answered_block: "notes" })) close();
  };

  return (
    <>
      <EditableBlock
        title={t(`numberRequests.details.${request.kind}.notes`)}
        description={
          isSetup ? t("numberRequests.details.sip_trunk.intro") : undefined
        }
        filled={request.notes.trim() !== ""}
        // A SIP request's setup description is the request, so an empty one is
        // never "answered" — it stays open until it says something.
        answered={!isSetup && isBlockAnswered(request, "notes")}
        optional={!isSetup}
        readOnly={readOnly}
        summary={
          request.notes.trim() ? (
            <p className="text-sm whitespace-pre-wrap">{request.notes}</p>
          ) : undefined
        }
      >
        {(close) => (
          <form
            onSubmit={(event) => submit(event, close)}
            className="space-y-3"
          >
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t(
                `numberRequests.details.${request.kind}.notesPlaceholder`,
              )}
              rows={isSetup ? 6 : 3}
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={isSaving}>
                {isSaving
                  ? t("numberRequests.details.saving")
                  : t("numberRequests.details.save")}
              </Button>
            </div>
          </form>
        )}
      </EditableBlock>
    </>
  );
}
